/**
 * Surge Detection — flags when event volume for a source type exceeds
 * 3x the rolling 7-day baseline within a 1-hour window.
 *
 * Algorithm:
 * 1. Maintain rolling baseline: count events by type per 1-hour window, keep 7-day history
 * 2. Current window = events from last hour grouped by type
 * 3. Surge triggered when: current >= 3x baseline AND count >= 5 AND 2+ distinct sources
 * 4. Track surges by region using continent-level geographic bucketing
 */

import type { NormalizedEvent } from '@/sources/base-source';
import type { SurgeEvent } from './types';
import { getEventTimestamp } from './types';

const MAX_WINDOWS = 168; // 7 days * 24 hours
const ONE_HOUR_MS = 60 * 60 * 1000;
const SEVEN_DAYS_MS = 7 * 24 * ONE_HOUR_MS;
const MIN_WINDOWS_FOR_DETECTION = 6;
const SURGE_THRESHOLD_RATIO = 3;
const SURGE_MIN_COUNT = 5;
const SURGE_MIN_SOURCES = 2;

/** Floor a timestamp to the start of its hour. */
function floorToHour(ms: number): number {
  return Math.floor(ms / ONE_HOUR_MS) * ONE_HOUR_MS;
}

/**
 * Rolling event history — maintains 1-hour windows for up to 7 days.
 * Deduplicates events by ID to prevent double-counting across analysis cycles.
 */
export class EventHistory {
  private windowCounts: Map<number, Map<string, number>> = new Map();
  private processedIds: Set<string> = new Set();

  /** Types excluded from surge history — meaningless for volume anomaly detection. */
  private static readonly EXCLUDED_TYPES = new Set(['custom', 'vessel_position', 'flight_position']);

  /** Record new events into history windows. Skips already-processed events. */
  recordEvents(events: NormalizedEvent[]): void {
    const cutoff = Date.now() - SEVEN_DAYS_MS;

    for (const ev of events) {
      if (this.processedIds.has(ev.id)) continue;
      this.processedIds.add(ev.id);

      const ts = getEventTimestamp(ev);
      if (ts < cutoff) continue;
      if (EventHistory.EXCLUDED_TYPES.has(ev.type)) continue;

      const windowStart = floorToHour(ts);
      if (!this.windowCounts.has(windowStart)) {
        this.windowCounts.set(windowStart, new Map());
      }
      const window = this.windowCounts.get(windowStart)!;
      window.set(ev.type, (window.get(ev.type) ?? 0) + 1);
    }

    this.trimOldWindows();
    this.trimProcessedIds();
  }

  /** Get baseline average count per type from historical (non-current) windows. */
  getBaseline(): Map<string, number> {
    const baselines = new Map<string, number>();
    const currentWindowStart = floorToHour(Date.now());

    // Exclude the current (incomplete) window
    const historical: Map<string, number>[] = [];
    for (const [ts, counts] of this.windowCounts) {
      if (ts < currentWindowStart) {
        historical.push(counts);
      }
    }

    if (historical.length === 0) return baselines;

    const totals = new Map<string, number>();
    for (const counts of historical) {
      for (const [type, count] of counts) {
        totals.set(type, (totals.get(type) ?? 0) + count);
      }
    }

    for (const [type, total] of totals) {
      baselines.set(type, total / historical.length);
    }

    return baselines;
  }

  /** Number of recorded windows. */
  getWindowCount(): number {
    return this.windowCounts.size;
  }

  private trimOldWindows(): void {
    if (this.windowCounts.size <= MAX_WINDOWS) return;
    const sorted = Array.from(this.windowCounts.keys()).sort((a, b) => a - b);
    const excess = sorted.length - MAX_WINDOWS;
    for (let i = 0; i < excess; i++) {
      this.windowCounts.delete(sorted[i]);
    }
  }

  private trimProcessedIds(): void {
    if (this.processedIds.size <= 100_000) return;
    const arr = Array.from(this.processedIds);
    this.processedIds = new Set(arr.slice(-50_000));
  }
}

// Continent mapping by country code for regional bucketing
const CONTINENT_MAP: Record<string, string> = {
  // Africa
  DZ: 'Africa', AO: 'Africa', BJ: 'Africa', BW: 'Africa', BF: 'Africa',
  BI: 'Africa', CM: 'Africa', CV: 'Africa', CF: 'Africa', TD: 'Africa',
  KM: 'Africa', CG: 'Africa', CD: 'Africa', CI: 'Africa', DJ: 'Africa',
  EG: 'Africa', GQ: 'Africa', ER: 'Africa', SZ: 'Africa', ET: 'Africa',
  GA: 'Africa', GM: 'Africa', GH: 'Africa', GN: 'Africa', GW: 'Africa',
  KE: 'Africa', LS: 'Africa', LR: 'Africa', LY: 'Africa', MG: 'Africa',
  MW: 'Africa', ML: 'Africa', MR: 'Africa', MU: 'Africa', MA: 'Africa',
  MZ: 'Africa', NA: 'Africa', NE: 'Africa', NG: 'Africa', RW: 'Africa',
  ST: 'Africa', SN: 'Africa', SC: 'Africa', SL: 'Africa', SO: 'Africa',
  ZA: 'Africa', SS: 'Africa', SD: 'Africa', TZ: 'Africa', TG: 'Africa',
  TN: 'Africa', UG: 'Africa', ZM: 'Africa', ZW: 'Africa',
  // Asia
  AF: 'Asia', AM: 'Asia', AZ: 'Asia', BH: 'Asia', BD: 'Asia', BT: 'Asia',
  BN: 'Asia', KH: 'Asia', CN: 'Asia', GE: 'Asia', IN: 'Asia', ID: 'Asia',
  IR: 'Asia', IQ: 'Asia', IL: 'Asia', JP: 'Asia', JO: 'Asia', KZ: 'Asia',
  KW: 'Asia', KG: 'Asia', LA: 'Asia', LB: 'Asia', MY: 'Asia', MV: 'Asia',
  MN: 'Asia', MM: 'Asia', NP: 'Asia', KP: 'Asia', KR: 'Asia', OM: 'Asia',
  PK: 'Asia', PS: 'Asia', PH: 'Asia', QA: 'Asia', SA: 'Asia', SG: 'Asia',
  LK: 'Asia', SY: 'Asia', TW: 'Asia', TJ: 'Asia', TH: 'Asia', TL: 'Asia',
  TM: 'Asia', AE: 'Asia', UZ: 'Asia', VN: 'Asia', YE: 'Asia',
  // Europe
  AL: 'Europe', AD: 'Europe', AT: 'Europe', BY: 'Europe', BE: 'Europe',
  BA: 'Europe', BG: 'Europe', HR: 'Europe', CY: 'Europe', CZ: 'Europe',
  DK: 'Europe', EE: 'Europe', FI: 'Europe', FR: 'Europe', DE: 'Europe',
  GR: 'Europe', HU: 'Europe', IS: 'Europe', IE: 'Europe', IT: 'Europe',
  XK: 'Europe', LV: 'Europe', LI: 'Europe', LT: 'Europe', LU: 'Europe',
  MT: 'Europe', MD: 'Europe', MC: 'Europe', ME: 'Europe', NL: 'Europe',
  MK: 'Europe', NO: 'Europe', PL: 'Europe', PT: 'Europe', RO: 'Europe',
  RU: 'Europe', SM: 'Europe', RS: 'Europe', SK: 'Europe', SI: 'Europe',
  ES: 'Europe', SE: 'Europe', CH: 'Europe', TR: 'Europe', UA: 'Europe',
  GB: 'Europe', VA: 'Europe',
  // Americas
  AG: 'Americas', AR: 'Americas', BS: 'Americas', BB: 'Americas',
  BZ: 'Americas', BO: 'Americas', BR: 'Americas', CA: 'Americas',
  CL: 'Americas', CO: 'Americas', CR: 'Americas', CU: 'Americas',
  DM: 'Americas', DO: 'Americas', EC: 'Americas', SV: 'Americas',
  GD: 'Americas', GT: 'Americas', GY: 'Americas', HT: 'Americas',
  HN: 'Americas', JM: 'Americas', MX: 'Americas', NI: 'Americas',
  PA: 'Americas', PY: 'Americas', PE: 'Americas', KN: 'Americas',
  LC: 'Americas', VC: 'Americas', SR: 'Americas', TT: 'Americas',
  US: 'Americas', UY: 'Americas', VE: 'Americas',
  // Oceania
  AU: 'Oceania', FJ: 'Oceania', KI: 'Oceania', MH: 'Oceania',
  FM: 'Oceania', NR: 'Oceania', NZ: 'Oceania', PW: 'Oceania',
  PG: 'Oceania', WS: 'Oceania', SB: 'Oceania', TO: 'Oceania',
  TV: 'Oceania', VU: 'Oceania',
};

/** Determine region for an event using country code or latitude fallback. */
function getRegion(event: NormalizedEvent): string {
  const code = event.location?.countryCode?.toUpperCase();
  if (code && CONTINENT_MAP[code]) return CONTINENT_MAP[code];

  const lat = event.location?.lat;
  if (lat == null) return 'Unknown';
  if (lat > 35) return 'Northern Hemisphere';
  if (lat < -35) return 'Southern Hemisphere';
  return 'Tropics';
}

/**
 * Detect event volume surges — flags when a type's current count
 * exceeds 3x its rolling average with 5+ events from 2+ sources.
 */
export function detectSurges(
  events: NormalizedEvent[],
  history: EventHistory,
): SurgeEvent[] {
  // Record all events into history (deduplicates internally by ID)
  history.recordEvents(events);

  // Need sufficient history before detecting surges
  if (history.getWindowCount() < MIN_WINDOWS_FOR_DETECTION) return [];

  const baselines = history.getBaseline();

  // Count events in the current hour by type
  const oneHourAgo = Date.now() - ONE_HOUR_MS;
  const currentEvents = events.filter(
    (e) => getEventTimestamp(e) >= oneHourAgo,
  );

  if (currentEvents.length === 0) return [];

  const currentCounts = new Map<
    string,
    { count: number; sources: Set<string>; events: NormalizedEvent[] }
  >();

  // Types excluded from surge detection — 'custom' is the default for all RSS events
  // and produces meaningless surges. Other types are sensor/position data.
  const SURGE_EXCLUDED_TYPES = new Set(['custom', 'vessel_position', 'flight_position']);

  for (const ev of currentEvents) {
    if (SURGE_EXCLUDED_TYPES.has(ev.type)) continue;
    if (!currentCounts.has(ev.type)) {
      currentCounts.set(ev.type, { count: 0, sources: new Set(), events: [] });
    }
    const entry = currentCounts.get(ev.type)!;
    entry.count++;
    entry.sources.add(ev.source);
    entry.events.push(ev);
  }

  const surges: SurgeEvent[] = [];

  for (const [type, data] of currentCounts) {
    const baseline = baselines.get(type) ?? 0;

    // Surge conditions: >= 3x baseline, >= 5 events, 2+ distinct sources
    if (
      baseline > 0 &&
      data.count >= SURGE_THRESHOLD_RATIO * baseline &&
      data.count >= SURGE_MIN_COUNT &&
      data.sources.size >= SURGE_MIN_SOURCES
    ) {
      // Determine dominant region
      const regionCounts = new Map<string, number>();
      for (const ev of data.events) {
        const region = getRegion(ev);
        regionCounts.set(region, (regionCounts.get(region) ?? 0) + 1);
      }
      const topRegion =
        Array.from(regionCounts.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ??
        'Global';

      surges.push({
        sourceType: type,
        count: data.count,
        baseline: Math.round(baseline * 10) / 10,
        ratio: Math.round((data.count / baseline) * 10) / 10,
        region: topRegion,
      });
    }
  }

  // Sort by ratio descending (most anomalous first)
  surges.sort((a, b) => b.ratio - a.ratio);

  return surges;
}
